import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { LoadingButton } from "@/components/ui/loading-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Shield } from "lucide-react";

interface MFAVerificationProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export const MFAVerification = ({ onSuccess, onCancel }: MFAVerificationProps) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [factorId, setFactorId] = useState<string>("");
  const [challengeId, setChallengeId] = useState<string>("");
  const { toast } = useToast();

  useEffect(() => {
    setupChallenge();
  }, []);

  const setupChallenge = async () => {
    try {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const verifiedFactor = factors?.totp.find(
        (factor) => factor.status === 'verified'
      );

      if (!verifiedFactor) {
        throw new Error("No MFA factor found");
      }

      setFactorId(verifiedFactor.id);

      // Create challenge
      const challenge = await supabase.auth.mfa.challenge({ 
        factorId: verifiedFactor.id 
      });

      if (challenge.error) throw challenge.error;
      if (challenge.data) {
        setChallengeId(challenge.data.id);
      }
    } catch (error: any) {
      console.error("Error setting up MFA challenge:", error);
      toast({
        title: "Error",
        description: "Failed to setup MFA verification",
        variant: "destructive",
      });
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) return;

    try {
      setLoading(true);

      const { error } = await supabase.auth.mfa.verify({
        factorId: factorId,
        challengeId: challengeId,
        code: code,
      });

      if (error) throw error;

      toast({
        title: "Verification Successful",
        description: "You've been authenticated successfully.",
      });

      onSuccess();
    } catch (error: any) {
      toast({
        title: "Verification Failed",
        description: error.message || "Invalid verification code",
        variant: "destructive",
      });
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-4">
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center">
            <Shield className="w-8 h-8 text-primary-foreground" />
          </div>
        </div>
        <div className="text-center space-y-2">
          <CardTitle className="text-2xl font-bold">Two-Factor Authentication</CardTitle>
          <CardDescription className="text-base">
            Enter the 6-digit code from your authenticator app
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="mfa-code">Authentication Code</Label>
          <Input
            id="mfa-code"
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            maxLength={6}
            className="text-center text-2xl tracking-widest"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && code.length === 6) {
                handleVerify();
              }
            }}
          />
        </div>

        <div className="flex flex-col gap-2">
          <LoadingButton
            onClick={handleVerify}
            disabled={code.length !== 6}
            loading={loading}
            loadingText="Verifying…"
            className="w-full"
          >
            Verify Code
          </LoadingButton>
          
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={loading}
            className="w-full"
          >
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
